// auto created file
import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { useProfile } from "../../../hooks/useProfile";
import {
  normalizeStateKey,
  buildDepartmentStatsFromEmployees,
  flattenHierarchyNodes,
  extractEmployeesFromPayload,
  extractWorkingCompanyName,
  getStoredUserInfo,
  getStoredAdminLevel,
  getDepartmentStatsArray,
} from "../utils/employeeUtils";
import { COLOR_PALETTE } from "../constants/colors";

const useDashboardData = (propEmployeeCount, propCounts, propDepartmentStats, BASE_URL) => {
  const { profile } = useProfile();
  const [employeeCount, setEmployeeCount] = useState(
    typeof propEmployeeCount === "number" ? propEmployeeCount : 0
  );
  const [counts, setCounts] = useState(
    propCounts && Object.keys(propCounts).length
      ? { ...propCounts }
      : { Active: 0, OnLeave: 0, Recent: 0, TotalCompanies: 0 }
  );
  const [departmentStats, setDepartmentStats] = useState(
    Array.isArray(propDepartmentStats) ? [...propDepartmentStats] : []
  );
  const [userName, setUserName] = useState("User");
  const [userArabicName, setUserArabicName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);

  // Safely decode JWT payload without throwing on malformed tokens
  const safeDecodeToken = (token) => {
    try {
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length < 2) return null;
      let payloadStr = parts[1];
      // Fix URL-safe base64
      payloadStr = payloadStr.replace(/-/g, '+').replace(/_/g, '/');
      const pad = payloadStr.length % 4;
      if (pad) payloadStr += '='.repeat(4 - pad);
      const json = atob(payloadStr);
      return JSON.parse(json);
    } catch (err) {
      console.warn('safeDecodeToken failed:', err);
      return null;
    }
  };

  // Sync incoming props to local state when parent updates them
  useEffect(() => {
    // Only apply incoming prop values if local fetched values are still empty/zero.
    // This prevents stale parent props (e.g. from navigating between pages)
    // from overwriting freshly fetched totals from the server.
    if (typeof propEmployeeCount === "number" && employeeCount === 0) {
      setEmployeeCount(propEmployeeCount);
    }
    if (propCounts && Object.keys(propCounts).length) {
      const currentCountsEmpty =
        !counts ||
        ((Number(counts.Active) === 0 || counts.Active === undefined) &&
          (Number(counts.OnLeave) === 0 || counts.OnLeave === undefined) &&
          (Number(counts.Recent) === 0 || counts.Recent === undefined) &&
          (Number(counts.TotalCompanies) === 0 || counts.TotalCompanies === undefined));

      if (currentCountsEmpty) {
        setCounts({ ...propCounts });
      }
    }
  }, [propEmployeeCount, propCounts, employeeCount, counts]);

  useEffect(() => {
    if (!Array.isArray(propDepartmentStats)) return;
    setDepartmentStats([...propDepartmentStats]);
  }, [propDepartmentStats]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const payload = safeDecodeToken(token) || {};
          // Keep clientName in localStorage (used by other parts of the app)
          if (payload.clientName) {
            localStorage.setItem("clientName", payload.clientName);
          }
        }

        // Use profile data already fetched by useProfile hook
        if (profile) {
          const englishName = profile.employeeName?.split(" ")[0] || "User";
          setUserName(englishName);
          const firstArabicName = profile.employeeArabicName?.split(" ")[0] || "";
          setUserArabicName(firstArabicName);
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };

    fetchUserData();
  }, [BASE_URL, propEmployeeCount, propCounts, propDepartmentStats, profile]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      // If parent passed counts/employeeCount, prefer those values
      // and skip internal fetching to avoid race/overwrite issues.
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }

        // Decode token to get user role and infinity code (safely)
        const payload = safeDecodeToken(token);
        if (!payload) {
          throw new Error("Invalid authentication token");
        }

        const storedUserInfo = getStoredUserInfo();
        const roleCandidates = [
          payload.role,
          profile?.role,
        ].filter((value) => typeof value === "string" && value.trim().length > 0);

        const resolvedRoleRaw = roleCandidates.length ? roleCandidates[0] : "user";
        const normalizedRole = resolvedRoleRaw.toString().trim().toLowerCase();
        try {
          localStorage.setItem("role", normalizedRole);
        } catch (error) {
          console.warn("Failed to persist resolved role:", error);
        }

        const userInfinityCode = payload.infinityCode || localStorage.getItem("infinityCode");
        const tokenClientName = payload.clientName || payload.client || payload.companyName;
        const storedClientName = profile?.clientName;
        const clientName = storedClientName || tokenClientName || "Infinity";
        if (!storedClientName && tokenClientName) {
          localStorage.setItem("clientName", tokenClientName);
        }
        const isInfinity = clientName?.toLowerCase() === "infinity";
        const encodedClientName = encodeURIComponent(clientName);

        const adminLevelCandidate =
          profile?.adminLevel ||
          storedUserInfo?.adminLevel ||
          storedUserInfo?.permissions?.adminLevel ||
          getStoredAdminLevel();
        const adminLevelIdFromUser =
          profile?.adminLevelId ||
          storedUserInfo?.adminLevelId ||
          storedUserInfo?.admin_level_id ||
          adminLevelCandidate?.adminLevelId ||
          adminLevelCandidate?.id;

        const hasAdminLevel = Boolean(
          adminLevelIdFromUser ||
          (Array.isArray(adminLevelCandidate?.modules) && adminLevelCandidate.modules.length > 0)
        );

        const storedViewTypeRaw = (localStorage.getItem("managerViewType") || localStorage.getItem("viewType") || "").toLowerCase();
        let effectiveViewType = storedViewTypeRaw;

        if (!effectiveViewType) {
          effectiveViewType = normalizedRole === "manager" ? "manager" : normalizedRole || "user";
        }

        if (normalizedRole === "manager" && effectiveViewType !== "manager" && !hasAdminLevel) {
          effectiveViewType = "manager";
        }

        try {
          localStorage.setItem("managerViewType", effectiveViewType);
          localStorage.setItem("viewType", effectiveViewType);
        } catch (error) {
          console.warn("Failed to persist view type:", error);
        }
        const adminViewActive = effectiveViewType === "admin" && hasAdminLevel;
        const shouldUseManagerHierarchy =
          !adminViewActive && effectiveViewType === "manager";
        const isAdminRole = normalizedRole === "admin" || normalizedRole === "superadmin";

        const externalDataProvided =
          typeof propEmployeeCount === "number" &&
          propCounts &&
          Object.keys(propCounts).length > 0;

        if (!adminViewActive && !shouldUseManagerHierarchy && externalDataProvided) {
          setEmployeeCount(propEmployeeCount);
          setCounts({ ...propCounts });
          setDepartmentStats(
            Array.isArray(propDepartmentStats) ? [...propDepartmentStats] : []
          );
          setLoading(false);
          return;
        }

        if (adminViewActive) {
          const visibilityClientName = adminLevelCandidate?.clientName || clientName;
          const query = visibilityClientName
            ? `?clientName=${encodeURIComponent(visibilityClientName)}`
            : "";

          const visibilityResponse = await apiFetch(
            `${BASE_URL}/employees/visibility${query}`,
            { method: "GET", headers: { "x-view-type": "admin" } }
          );

          if (!visibilityResponse.ok) {
            throw new Error("Failed to fetch visibility-scoped employees");
          }

          const visibilityPayload = await visibilityResponse.json();
          const visibleEmployees = extractEmployeesFromPayload(visibilityPayload);
          const totalVisible = visibleEmployees.length;
          const activeCount = visibleEmployees.filter(
            (emp) => normalizeStateKey(emp) === "active"
          ).length;
          const onLeaveCount = visibleEmployees.filter(
            (emp) => normalizeStateKey(emp) === "onleave"
          ).length;
          const companySet = new Set(
            visibleEmployees
              .map((emp) => {
                const source =
                  emp?.workingCompany ?? emp?.company ?? emp?.contractCompany ?? "";
                if (typeof source === "string") return source.trim();
                return String(source || "").trim();
              })
              .filter((name) => name.length > 0)
          );

          setEmployeeCount(totalVisible);
          setCounts({
            Active: activeCount,
            OnLeave: onLeaveCount,
            Recent: 0,
            TotalCompanies: companySet.size || (totalVisible ? 1 : 0),
          });

          setDepartmentStats(buildDepartmentStatsFromEmployees(visibleEmployees, totalVisible));
          setRecentActivities([]);
          setLoading(false);
          return;
        }

        if (shouldUseManagerHierarchy) {
          console.log("📊 Fetching manager hierarchy data...");

          if (!userInfinityCode) {
            throw new Error("No manager infinity code available for hierarchy fetch");
          }

          const encodedManagerCode = encodeURIComponent(userInfinityCode);
          const hierarchyUrl = `${BASE_URL}/employees/clients/${encodedClientName}/hierarchy?managerInfinityCode=${encodedManagerCode}`;

          const hierarchyResponse = await apiFetch(hierarchyUrl, { method: "GET" });

          if (!hierarchyResponse.ok) {
            throw new Error("Failed to fetch hierarchy data");
          }

          const hierarchyResult = await hierarchyResponse.json();
          const baseHierarchy = Array.isArray(hierarchyResult?.hierarchy)
            ? hierarchyResult.hierarchy
            : [];
          const flattenedHierarchy = flattenHierarchyNodes(baseHierarchy);
          const managerCodeSafe = typeof userInfinityCode === "string" ? userInfinityCode : String(userInfinityCode || "");
          const subordinateList = flattenedHierarchy.filter((node, index) => {
            const code =
              typeof node?.infinityCode === "string"
                ? node.infinityCode
                : String(node?.infinityCode || node?.employeeCode || node?.id || "");
            if (index === 0 && code === managerCodeSafe) {
              return false;
            }
            return managerCodeSafe ? code !== managerCodeSafe : true;
          });

          console.log(`👥 Found ${subordinateList.length} subordinates after flattening`);

          const totalEmployees = subordinateList.length;
          const activeCount = subordinateList.filter(
            (emp) => normalizeStateKey(emp) === "active"
          ).length;
          const onLeaveCount = subordinateList.filter(
            (emp) => normalizeStateKey(emp) === "onleave"
          ).length;
          const companySet = new Set(
            subordinateList
              .map((emp) => {
                const source =
                  emp?.workingCompany ?? emp?.company ?? emp?.contractCompany ?? "";
                if (typeof source === "string") return source.trim();
                return String(source || "").trim();
              })
              .filter((name) => name.length > 0)
          );

          setEmployeeCount(totalEmployees);
          setCounts({
            Active: activeCount,
            OnLeave: onLeaveCount,
            Recent: 0,
            TotalCompanies: companySet.size || (totalEmployees ? 1 : 0),
          });

          setDepartmentStats(buildDepartmentStatsFromEmployees(subordinateList, totalEmployees));
          setRecentActivities([]);
          setLoading(false);
          return;
        }

        if (isAdminRole) {
          console.log("📊 Fetching admin dashboard data...");

          const employeeCountUrl = isInfinity
            ? `${BASE_URL}/employees/count`
            : `${BASE_URL}/employees/client/${encodedClientName}/count`;

          const activeUrl = isInfinity
            ? `${BASE_URL}/employees/Active`
            : `${BASE_URL}/employees/client/${encodedClientName}/Active`;

          const onLeaveUrl = isInfinity
            ? `${BASE_URL}/employees/OnLeave`
            : `${BASE_URL}/employees/client/${encodedClientName}/OnLeave`;

          const deptStatsUrl = isInfinity
            ? `${BASE_URL}/employees/departments/stats`
            : `${BASE_URL}/employees/client/${encodedClientName}/departments/stats`;

          const activitiesUrl = isInfinity
            ? `${BASE_URL}/employees/activities/recent`
            : `${BASE_URL}/employees/client/${encodedClientName}/activities/recent`;
          const employeesByClientUrl = `${BASE_URL}/employees/clients/${encodedClientName}`;

          const [
            employeeCountRes,
            activeRes,
            onLeaveRes,
            deptStatsRes,
            activitiesRes,
            totalCompaniesRes,
            employeesByClientRes
          ] = await Promise.all([
            apiFetch(employeeCountUrl, { method: "GET" }),
            apiFetch(activeUrl, { method: "GET" }),
            apiFetch(onLeaveUrl, { method: "GET" }),
            apiFetch(deptStatsUrl, { method: "GET" }),
            apiFetch(activitiesUrl, { method: "GET" }),
            apiFetch(`${BASE_URL}/employees/clients/all`, { method: "GET" }),
            apiFetch(employeesByClientUrl, { method: "GET" })
          ]);

          if (
            !employeeCountRes.ok ||
            !activeRes.ok ||
            !onLeaveRes.ok ||
            !deptStatsRes.ok ||
            !activitiesRes.ok ||
            !employeesByClientRes.ok
          ) {
            throw new Error("Failed to fetch dashboard data");
          }

          const [
            employeeCountData,
            activeData,
            onLeaveData,
            deptData,
            activitiesData,
            totalCompaniesData,
            employeesByClientData
          ] = await Promise.all([
            employeeCountRes.json(),
            activeRes.json(),
            onLeaveRes.json(),
            deptStatsRes.json(),
            activitiesRes.json(),
            totalCompaniesRes.json(),
            employeesByClientRes.json()
          ]);

          const employeesForCompanyCount = extractEmployeesFromPayload(employeesByClientData);
          const workingCompaniesSet = new Set(
            employeesForCompanyCount
              .map((emp) => extractWorkingCompanyName(emp))
              .filter((name) => name.length > 0)
          );
          const workingCompaniesCount = workingCompaniesSet.size;

          setEmployeeCount(employeeCountData.count);
          setCounts({
            Active: activeData.count,
            OnLeave: onLeaveData.count,
            Recent: activitiesData.length,
            TotalCompanies:
              workingCompaniesCount > 0
                ? workingCompaniesCount
                : isInfinity
                  ? totalCompaniesData.count
                  : employeesForCompanyCount.length > 0
                    ? 1
                    : 0
          });

          const totalEmployeesCount = Number(employeeCountData.count) || 0;
          const normalizedDeptData = getDepartmentStatsArray(deptData);
          const mappedDeptStats = normalizedDeptData
            .map((dept, idx) => {
              const count = Number(
                dept.count ?? dept.total ?? dept.employeeCount ?? dept.employees ?? dept.value
              ) || 0;
              const percentage = dept.percentage ?? (totalEmployeesCount > 0 ? Math.round((count / totalEmployeesCount) * 100) : 0);
              const name = dept.name || dept.department || dept.label || dept.departmentName || "Unassigned";
              return {
                name,
                count,
                percentage,
                color: COLOR_PALETTE[idx % COLOR_PALETTE.length],
              };
            })
            .filter((dept) => dept.count > 0);

          setDepartmentStats(mappedDeptStats);
          setRecentActivities(activitiesData);
          setLoading(false);
          return;
        }

        console.log("👤 Regular employee - no dashboard data");
        setLoading(false);
        return;

      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [BASE_URL, propEmployeeCount, propCounts, propDepartmentStats, profile]);

  return {
    employeeCount,
    counts,
    departmentStats,
    userName,
    userArabicName,
    loading,
    error,
    recentActivities,
  };
};

export default useDashboardData;